import axiosInstance from '../api';
import {
  VideoMaterial,
  GetVideoMaterialsRes,
  CreateVideoMaterialRes,
  DeleteVideoMaterialRes,
} from './videoMaterialApi.types';

/**
 * 動画に紐づく資料一覧を取得（メタデータのみ）
 *
 * デモモードの汎用フォールバック（demoAdapter.ts の「未ハンドルのルート」処理）は
 * GET リクエストに素の配列 `[]` を返す（`{ videoMaterials: [] }` の形ではない）。
 * 本番形状・デモのフォールバック形状のどちらが来ても配列を返すようにし、
 * `response.data.videoMaterials` が undefined になって呼び出し側が落ちるのを防ぐ。
 */
export const fetchVideoMaterials = async (videoId: string): Promise<VideoMaterial[]> => {
  const response = await axiosInstance.get<GetVideoMaterialsRes | VideoMaterial[]>(`/videos/${videoId}/materials`);
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  }
  return Array.isArray(data?.videoMaterials) ? data.videoMaterials : [];
};

/**
 * 動画に資料（PDF）を追加する
 */
export const createVideoMaterial = async (
  videoId: string,
  file: File
): Promise<VideoMaterial> => {
  const formData = new FormData();
  formData.append('material', file);

  const response = await axiosInstance.post<CreateVideoMaterialRes>(
    `/videos/${videoId}/materials`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.videoMaterial;
};

/**
 * 資料を削除する
 */
export const deleteVideoMaterial = async (id: number): Promise<DeleteVideoMaterialRes> => {
  const response = await axiosInstance.delete<DeleteVideoMaterialRes>(`/video-materials/${id}`);
  return response.data;
};

/**
 * 資料の内容（PDFバイト列）を閲覧用に取得する。院外制限なし。
 *
 * pdf.js の getDocument({ data }) にそのまま渡せる ArrayBuffer で返す。
 * responseType: 'blob' + Blob.arrayBuffer() ではなく最初から 'arraybuffer' を使うのは、
 * jsdom（単体テスト環境）の Blob 実装に arrayBuffer() が無く落ちるため。
 */
export const fetchVideoMaterialContent = async (id: number): Promise<ArrayBuffer> => {
  const response = await axiosInstance.get<ArrayBuffer>(`/video-materials/${id}/view`, {
    responseType: 'arraybuffer',
  });
  return response.data;
};

/** 資料の原本保存（ダウンロード）のURL。院内ネットワークからのみ200が返る */
export const getVideoMaterialDownloadUrl = (id: number): string => `/api/video-materials/${id}/download`;
